import React, { useState, useEffect, useMemo } from 'react';
import { getCards, getTotalPostos } from '../../services/api';
import { formatarLitros } from '../../utils/formatters';

// Função para categorizar produtos
const categorizarProduto = (nome) => {
    if (!nome || typeof nome !== 'string') return null;
    const n = nome.toUpperCase().trim();
    if (n.includes('GASOLINA')) return n.includes('ADITIVADA') ? 'Gasolina A' : 'Gasolina C';
    if (n.includes('MARITIMO')) return 'Diesel Marítimo';
    // Verificar OD B S antes da verificação de DIESEL, pois pode não conter a palavra "DIESEL"
    if (n.includes('OD B S')) {
        // Verificar se não é S500 primeiro
        if (n.includes('S500') || n.includes('S-500')) return 'Diesel S500';
        return 'Diesel S10';
    }
    if (n.includes('DIESEL')) {
        if (n.includes('S10') || n.includes('S-10')) return 'Diesel S10';
        if (n.includes('S500') || n.includes('S-500')) return 'Diesel S500';
    }
    if (n.includes('ETANOL')) return 'Etanol';
    return null;
};

// Função para processar dados da API
export const processarCardsCombustivel = (dadosAPI) => {
    if (!Array.isArray(dadosAPI) || dadosAPI.length === 0) return [];
    const map = new Map(['Gasolina C', 'Gasolina A', 'Diesel S500', 'Diesel S10', 'Diesel Marítimo', 'Etanol'].map(c => [c, { categoria: c, valor: 0, produtos: [] }]));
    dadosAPI.forEach((item) => {
        const nome = item['visao_geral.nome_produto'] ?? item.nome_produto ?? item['nome_produto'];
        const valor = item['visao_geral.quantidade'] ?? item['visao_geral.litros'] ?? item['visao_geral.indicador_selecionado'] ?? item['visao_geral.valor_total'] ?? item.quantidade ?? item.litros ?? item.valor ?? item.indicador_selecionado ?? item.valor_total;
        if (!nome || valor == null) return;
        const valorNum = Number(valor);
        if (isNaN(valorNum) || valorNum <= 0) return;
        const cat = categorizarProduto(nome);
        const data = map.get(cat);
        if (data) { data.valor += valorNum; data.produtos.push({ nome, valor: valorNum }); }
    });
    return Array.from(map.values()).filter(c => c.valor > 0);
};

const CardCombustivel = ({
    tipo = 'combustivel',
    categoria,
    label,
    cor,
    icon,
    filtros = {},
    className = '',
    // Se vier valor externo, o card vira 100% apresentacional (sem buscar API)
    valorExterno = undefined,
    loadingExterno = undefined,
}) => {
    const [loading, setLoading] = useState(true);
    const [valor, setValor] = useState(null);

    const filtrosSerializados = useMemo(() => JSON.stringify(filtros), [filtros]);

    useEffect(() => {
        // Modo otimizado: quando o pai já buscou os dados, não faz requisições aqui
        if (valorExterno !== undefined || loadingExterno !== undefined) {
            return;
        }

        const fetchDados = async () => {
            setLoading(true);
            try {
                if (tipo === 'total_litros') {
                    // Somar todos os valores dos cards de combustível
                    const dadosAPI = await getCards(filtros);
                    const cards = processarCardsCombustivel(dadosAPI);
                    const total = cards.reduce((acc, card) => acc + (card.valor || 0), 0);
                    setValor(total > 0 ? total : null);
                } else if (tipo === 'postos') {
                    // Buscar apenas o total de postos (look específico)
                    const total = await getTotalPostos(filtros);
                    setValor(total !== null && !isNaN(total) ? total : null);
                } else {
                    // Buscar dados de combustível específico
                    const dadosAPI = await getCards(filtros);
                    const cards = processarCardsCombustivel(dadosAPI);
                    const card = cards.find(card => card.categoria === categoria);
                    setValor(card && card.valor > 0 ? card.valor : null);
                }
            } catch (error) {
                setValor(null);
            } finally {
                setLoading(false);
            }
        };

        fetchDados();
    }, [filtrosSerializados, tipo, categoria, valorExterno, loadingExterno]);

    // Formatação do valor baseada no tipo
    const formatarValor = (val) => {
        if (tipo === 'postos') {
            return val;
        }
        return formatarLitros(val);
    };

    return (
        <div className={`bg-white p-3 border border-gray-300 ${className}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <div className={`w-6 h-6 rounded-lg ${cor} flex items-center justify-center`}>
                        {icon}
                    </div>
                    <h3 className="text-[1.00rem] font-bold text-gray-800">{label}</h3>
                </div>
            </div>

            <div className="mb-2">
                {(loadingExterno ?? loading) ? (
                    <div className="h-8 bg-gray-200 animate-pulse rounded"></div>
                ) : (valorExterno ?? valor) !== null ? (
                    <div className="text-[1.28rem] font-bold text-gray-800">
                        {formatarValor(valorExterno ?? valor)}
                    </div>
                ) : (
                    <div className="text-xs text-gray-500">Sem dados disponíveis</div>
                )}
            </div>
        </div>
    );
};

export default CardCombustivel;
